import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
  type InfiniteData,
} from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";
import type { PlanContentPost } from "./generated/api.schemas";

export interface ProductCategory {
  id: number;
  userId: number;
  name: string;
  createdAt: Date;
}

export interface AssetLibraryParams {
  page?: number;
  limit?: number;
  productId?: number;
  photoshootId?: number;
  shotType?: string;
  starred?: string;
  final?: string;
  month?: number;
  year?: number;
  source?: "all" | "generated" | "upsampled" | "edited" | "product" | "style-guide" | "refined" | "outfit";
  sort?: "newest" | "oldest";
  categoryId?: number;
  viewAll?: boolean;
  search?: string;
  editCallType?: string;
}

export interface AssetLibraryItem {
  id: number;
  imageUrl: string;
  isStarred: boolean;
  isFinal: boolean;
  createdAt: string;
  productId: number | null;
  productName: string | null;
  photoshootId: number | null;
  photoshootName: string | null;
  shotType: string | null;
  shotCardId: number | null;
  source: "generated" | "upsampled" | "edited" | "product" | "style-guide" | "refined";
  originalFilename?: string;
  upscaledWidth?: number;
  upscaledHeight?: number;
  isSoftDeleted?: boolean;
  imageType?: string;
  categoryId?: number | null;
  categoryName?: string | null;
  editCallType?: string | null;
  editPrompt?: string | null;
  isOutfit?: boolean;
}

export interface AssetLibraryResponse {
  assets: AssetLibraryItem[];
  total: number;
  page: number;
  totalPages: number;
}

export interface AssetFilterResponse {
  products: { id: number; name: string }[];
  photoshoots: { id: number; name: string; productId: number }[];
  categories: { id: number; name: string }[];
  allPhotoshoots: { id: number; name: string; productId: number }[];
}

export const getGetAssetsQueryKey = (params?: AssetLibraryParams) => [`/api/assets`, ...(params ? [params] : [])] as const;

function buildQueryString(params?: Record<string, string | number | boolean | undefined | null>): string {
  if (!params) return "";
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null);
  if (!entries.length) return "";
  return "?" + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join("&");
}

export function useGetAssets(params?: AssetLibraryParams, options?: { query?: Partial<UseQueryOptions<AssetLibraryResponse>> }) {
  const queryKey = getGetAssetsQueryKey(params);
  const queryFn = () => customFetch<AssetLibraryResponse>(`/api/assets${buildQueryString(params as Record<string, string | number | boolean | undefined | null>)}`, { method: "GET" });
  const queryOptions = { queryKey, queryFn, ...options?.query } as UseQueryOptions<AssetLibraryResponse>;
  const query = useQuery(queryOptions) as UseQueryResult<AssetLibraryResponse> & { queryKey: QueryKey };
  return { ...query, queryKey };
}

export const getGetAssetFiltersQueryKey = (viewAll?: boolean) => [`/api/assets/filters`, ...(viewAll ? [{ viewAll }] : [])] as const;

export function useGetAssetFilters(viewAll?: boolean, options?: { query?: Partial<UseQueryOptions<AssetFilterResponse>> }) {
  const queryKey = getGetAssetFiltersQueryKey(viewAll);
  const queryFn = () => customFetch<AssetFilterResponse>(`/api/assets/filters${viewAll ? "?viewAll=true" : ""}`, { method: "GET" });
  const queryOptions = { queryKey, queryFn, ...options?.query } as UseQueryOptions<AssetFilterResponse>;
  const query = useQuery(queryOptions) as UseQueryResult<AssetFilterResponse> & { queryKey: QueryKey };
  return { ...query, queryKey };
}

export function useStarAsset() {
  return useMutation({ mutationFn: ({ id, source }: { id: number; source?: string }) => customFetch(`/api/assets/${id}/star`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ source: source || "generated" }) }) });
}

export function useDeleteAsset() {
  return useMutation({ mutationFn: ({ id, source, permanent }: { id: number; source?: string; permanent?: boolean }) => {
    const params = new URLSearchParams();
    if (source) params.set("source", source);
    if (permanent) params.set("permanent", "true");
    const qs = params.toString();
    return customFetch(`/api/assets/${id}${qs ? `?${qs}` : ""}`, { method: "DELETE" });
  }});
}

export function useToggleFinalImage() {
  return useMutation({
    mutationFn: ({ id, source, photoshootId }: { id: number; source?: string; photoshootId?: number }) =>
      customFetch<{ isFinal: boolean; photoshootId?: number | null; photoshootName?: string | null }>(`/api/assets/${id}/final`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source, photoshootId }),
      }),
  });
}

export interface FinalImagesResponse {
  images: AssetLibraryItem[];
}

export const getGetFinalImagesQueryKey = (photoshootId: number) => [`/api/assets/final`, photoshootId] as const;

export function useGetFinalImages(photoshootId: number, options?: { query?: Partial<UseQueryOptions<FinalImagesResponse>> }) {
  const queryKey = getGetFinalImagesQueryKey(photoshootId);
  const queryFn = () => customFetch<FinalImagesResponse>(`/api/assets/final/${photoshootId}`, { method: "GET" });
  const queryOptions = { queryKey, queryFn, enabled: !!photoshootId, ...options?.query } as UseQueryOptions<FinalImagesResponse>;
  const query = useQuery(queryOptions) as UseQueryResult<FinalImagesResponse> & { queryKey: QueryKey };
  return { ...query, queryKey };
}

export interface UpscaleHistoryItem {
  id: number;
  originalImageUrl: string;
  originalObjectPath?: string;
  upscaledImageUrl: string;
  upscaledObjectPath?: string;
  originalFilename: string;
  originalWidth: number;
  originalHeight: number;
  upscaledWidth: number;
  upscaledHeight: number;
  model: string;
  upscaleFactor: number;
  creativity: number;
  detail: number;
  description: string | null;
  enhanceFaces: boolean;
  sourceType: string | null;
  createdAt: string;
}

export const getUpscaleHistoryQueryKey = () => [`/api/upsampler/history`] as const;

export function useGetUpscaleHistory(options?: { query?: Partial<UseQueryOptions<UpscaleHistoryItem[]>> }) {
  const queryKey = getUpscaleHistoryQueryKey();
  const queryFn = () => customFetch<UpscaleHistoryItem[]>(`/api/upsampler/history`, { method: "GET" });
  const queryOptions = { queryKey, queryFn, ...options?.query } as UseQueryOptions<UpscaleHistoryItem[]>;
  const query = useQuery(queryOptions) as UseQueryResult<UpscaleHistoryItem[]> & { queryKey: QueryKey };
  return { ...query, queryKey };
}

export interface UpscaleImageBody {
  imageUrl: string;
  originalFilename: string;
  originalWidth: number;
  originalHeight: number;
  upscaleFactor?: number;
  creativity?: number;
  detail?: number;
  description?: string;
  enhanceFaces?: boolean;
  model?: string;
}

export function useUpscaleImage() {
  return useMutation({
    mutationFn: (body: UpscaleImageBody) =>
      customFetch<UpscaleHistoryItem>(`/api/upsampler/upscale`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
  });
}

export const getProductCategoriesQueryKey = () => [`/api/product-categories`] as const;

export function useProductCategories(options?: { query?: Partial<UseQueryOptions<ProductCategory[]>> }) {
  const queryKey = getProductCategoriesQueryKey();
  const queryFn = () => customFetch<ProductCategory[]>(`/api/product-categories`, { method: "GET" });
  const queryOptions = { queryKey, queryFn, ...options?.query } as UseQueryOptions<ProductCategory[]>;
  const query = useQuery(queryOptions) as UseQueryResult<ProductCategory[]> & { queryKey: QueryKey };
  query.queryKey = queryOptions.queryKey as QueryKey;
  return query;
}

export function useCreateProductCategory() {
  return useMutation({
    mutationFn: (body: { name: string }) =>
      customFetch<ProductCategory>(`/api/product-categories`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
  });
}

export function useDeleteProductCategory() {
  return useMutation({
    mutationFn: (id: number) =>
      customFetch<void>(`/api/product-categories/${id}`, {
        method: "DELETE",
      }),
  });
}

export function useUpdateStyleGuidePhotos() {
  return useMutation({
    mutationFn: ({ id, styleGuidePhotos }: { id: number; styleGuidePhotos: string[] }) =>
      customFetch<{ id: number; styleGuidePhotos: string[] }>(`/api/style-guides/${id}/photos`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ styleGuidePhotos }),
      }),
  });
}

export function useUpdatePlatformStyleGuide() {
  return useMutation({
    mutationFn: ({ id, name, styleSummary }: { id: number; name?: string; styleSummary?: string | null }) =>
      customFetch<Record<string, unknown>>(`/api/style-guides/${id}/photos`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...(name !== undefined && { name }), ...(styleSummary !== undefined && { styleSummary }) }),
      }),
  });
}

export function useBulkAssignAssets() {
  return useMutation({
    mutationFn: ({ items, productId, categoryId }: {
      items: { id: number; source: string }[];
      productId?: number | null;
      categoryId?: number | null;
    }) =>
      customFetch<{ updated: number }>("/api/assets/bulk-assign", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items, productId, categoryId }),
      }),
  });
}

// ─── Plan Content paginated list (infinite query) ────────────────────────────
//
// Powers the Create Content tab's per-column infinite scroll. The legacy
// `planContentListPosts` endpoint stays unchanged so other surfaces
// (Post Planning's approved-post list, ImagePreviewModal, etc.) keep
// loading everything in one shot — only this tab pages.
//
// Query keys are namespaced under the same path the legacy hook uses:
//   [`/api/v1/influencers/${id}/plan-content/posts`, "page", column, filters]
//
// React-Query treats array query keys as a prefix tree, so every existing
// `qc.invalidateQueries({ queryKey: getPlanContentListPostsQueryKey(id) })`
// — which resolves to `[<path>]` — also invalidates the paginated queries
// for that influencer. No changes needed at any of the call sites.

export interface PlanContentListPostsPageParams {
  column: "draft" | "approved";
  showScheduled?: boolean;
  showPosted?: boolean;
  limit?: number;
}

export interface PlanContentListPostsPageResponse {
  posts: PlanContentPost[];
  nextCursor: number | null;
  total: number;
}

export const getPlanContentListPostsInfiniteQueryKey = (
  influencerId: number,
  params: PlanContentListPostsPageParams,
) =>
  [
    `/api/v1/influencers/${influencerId}/plan-content/posts`,
    "page",
    params.column,
    {
      showScheduled: params.showScheduled ?? true,
      showPosted: params.showPosted ?? true,
      limit: params.limit,
    },
  ] as const;

// ── First-page prefetch cache ─────────────────────────────────────────────
// Task #651: To make the Create Content tab paint instantly on navigation
// we kick off the FIRST page request as early as possible — typically from
// `prefetchByHref` when the user hovers / focuses the influencer profile
// link, well before React Query (and even React) have mounted.
//
// The in-flight promise lives in this module-level map, keyed by the same
// shape `usePlanContentListPostsInfinite`'s queryFn would have produced for
// pageParam=0. When the hook eventually mounts and runs its queryFn, it
// adopts the cached promise instead of issuing a duplicate request. The
// entry is dropped on consumption AND auto-expires after a short TTL so a
// stale prefetch doesn't shadow a real navigation a minute later.
//
// We never silently fall back if the prefetch fails — the queryFn just
// proceeds to do its own fetch. The user sees the normal loading state in
// that case, never a stale or broken response.
const FIRST_PAGE_PREFETCH_TTL_MS = 30_000;

interface FirstPagePrefetchEntry {
  promise: Promise<PlanContentListPostsPageResponse>;
  expiresAt: number;
}

const firstPagePrefetchCache = new Map<string, FirstPagePrefetchEntry>();

function firstPagePrefetchCacheKey(
  influencerId: number,
  params: PlanContentListPostsPageParams,
): string {
  return JSON.stringify([
    influencerId,
    params.column,
    params.showScheduled ?? true,
    params.showPosted ?? true,
    params.limit ?? null,
  ]);
}

function buildFirstPageUrl(
  influencerId: number,
  params: PlanContentListPostsPageParams,
): string {
  const qs = new URLSearchParams();
  qs.set("column", params.column);
  qs.set("cursor", "0");
  if (params.limit != null) qs.set("limit", String(params.limit));
  qs.set("showScheduled", (params.showScheduled ?? true) ? "true" : "false");
  qs.set("showPosted", (params.showPosted ?? true) ? "true" : "false");
  return `/api/v1/influencers/${influencerId}/plan-content/posts-page?${qs.toString()}`;
}

function takeFirstPagePrefetch(
  influencerId: number,
  params: PlanContentListPostsPageParams,
): Promise<PlanContentListPostsPageResponse> | null {
  const key = firstPagePrefetchCacheKey(influencerId, params);
  const entry = firstPagePrefetchCache.get(key);
  if (!entry) return null;
  firstPagePrefetchCache.delete(key);
  if (entry.expiresAt < Date.now()) return null;
  return entry.promise;
}

/**
 * Kick off the first page of the paginated `posts-page` endpoint for the
 * given influencer / column and stash the in-flight promise so a later
 * `usePlanContentListPostsInfinite` mount can adopt it without issuing a
 * second request. Safe to call repeatedly — duplicates within the TTL
 * resolve to the same in-flight promise.
 *
 * Defaults match the FE defaults (`showScheduled`/`showPosted` ON, server
 * default limit). Failures are swallowed: the eventual real query will
 * surface any error from its own fetch.
 */
export function prefetchPlanContentListPostsFirstPage(
  influencerId: number,
  params: PlanContentListPostsPageParams = { column: "draft" },
): void {
  if (typeof window === "undefined") return;
  if (!Number.isFinite(influencerId) || influencerId <= 0) return;
  const key = firstPagePrefetchCacheKey(influencerId, params);
  const existing = firstPagePrefetchCache.get(key);
  if (existing && existing.expiresAt >= Date.now()) return;
  const promise = customFetch<PlanContentListPostsPageResponse>(
    buildFirstPageUrl(influencerId, params),
    { method: "GET" },
  ).catch((err) => {
    // Drop the entry so a future prefetch can retry. Re-throw so any
    // queryFn that adopted this promise still sees the failure and falls
    // back to its normal error handling.
    firstPagePrefetchCache.delete(key);
    throw err;
  });
  firstPagePrefetchCache.set(key, {
    promise,
    expiresAt: Date.now() + FIRST_PAGE_PREFETCH_TTL_MS,
  });
}

export function usePlanContentListPostsInfinite(
  influencerId: number,
  params: PlanContentListPostsPageParams,
  options?: {
    query?: Omit<
      UseInfiniteQueryOptions<PlanContentListPostsPageResponse, Error>,
      "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
    >;
  },
): UseInfiniteQueryResult<InfiniteData<PlanContentListPostsPageResponse, number>, Error> & {
  queryKey: QueryKey;
} {
  const queryKey = getPlanContentListPostsInfiniteQueryKey(influencerId, params);
  const showScheduled = params.showScheduled ?? true;
  const showPosted = params.showPosted ?? true;
  const limit = params.limit;
  const result = useInfiniteQuery<PlanContentListPostsPageResponse, Error>({
    queryKey,
    initialPageParam: 0,
    queryFn: ({ pageParam = 0, signal }: { pageParam?: unknown; signal?: AbortSignal }) => {
      const cursor = Number(pageParam ?? 0) || 0;
      // Task #651: the first page is the one users wait on for first paint,
      // so we let `prefetchPlanContentListPostsFirstPage` race ahead from
      // `prefetchByHref` (hover/focus) and adopt that in-flight promise
      // here. Cancellation via `signal` doesn't apply to a prefetch we
      // started outside of React Query's lifecycle, but that's intentional —
      // the prefetch is short-lived and the response is cached for the
      // (very common) case where the user actually navigates.
      if (cursor === 0) {
        const prefetched = takeFirstPagePrefetch(influencerId, {
          column: params.column,
          showScheduled,
          showPosted,
          limit,
        });
        if (prefetched) return prefetched;
      }
      const qs = new URLSearchParams();
      qs.set("column", params.column);
      qs.set("cursor", String(cursor));
      if (limit != null) qs.set("limit", String(limit));
      qs.set("showScheduled", showScheduled ? "true" : "false");
      qs.set("showPosted", showPosted ? "true" : "false");
      return customFetch<PlanContentListPostsPageResponse>(
        `/api/v1/influencers/${influencerId}/plan-content/posts-page?${qs.toString()}`,
        { method: "GET", signal },
      );
    },
    getNextPageParam: (lastPage: PlanContentListPostsPageResponse) =>
      lastPage.nextCursor ?? undefined,
    ...(options?.query as object),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- useInfiniteQuery options type is over-constrained for our merged opts
  } as any);
  return Object.assign(
    result as UseInfiniteQueryResult<
      InfiniteData<PlanContentListPostsPageResponse, number>,
      Error
    >,
    { queryKey },
  );
}
